import React from 'react';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import Classnames from 'classnames';
import {
  extent,
  scaleLinear,
  scaleBand,
  line,
  format,
} from 'd3';

import './style.css';

const data = (_, props) => props.data;
const x = (_, props) => props.x;
const y = (_, props) => props.y;
const numTicks = (_, props) => props.numTicks || 6;

const width = (_, props) => props.width;
const gutter = state => state.gutter || 0;
const totalSize = createSelector([width], width => [width, 400]);
const size = createSelector(
  [totalSize, gutter],
  (totalSize, gutter) => 
    [ totalSize[0] - (gutter / 2), totalSize[1] ]
);
const margin = (_, props) => props.margin || { left: 50, top: 10, bottom: 30, right: 20 };

const yScale = createSelector(
  [data, size, margin],
  (data, size, margin) =>
    side => 
      scaleBand()
        .domain(data[(data.original.length >= data.proposed.length ? 'original' : 'proposed')].map((d,i) => i).reverse())
        .range([size[1] - (margin.bottom || 0), margin.top || 0])
        .paddingInner(0.4)
        .paddingOuter(0.4)
);

const xScale = createSelector(
  [data, x, size, margin, numTicks],
  (data, x, size, margin, numTicks) => 
    scaleLinear()
      .domain(extent([
        0, 
        ...data.original.map(d => d.value),
        ...data.proposed.map(d => d.value)
      ]))
      .clamp(true)
      .nice(numTicks)
      .range([margin.left || 0, size[0] - (margin.right || 0)])
);

const XAxis = ({ scale, gridHeight, format, numTicks }) => {
  const tickFormat = scale.tickFormat(numTicks, format || '.1s');
  return (
    <g className="axis">
      {scale.nice(numTicks).ticks(numTicks).map(tick => (
        <g key={tick} transform={`translate(${scale(tick)},0)`}>
          <line y2={6} />
          <line y2={-gridHeight} className="grid" />
          <text y={5} textAnchor="middle" dy="1em">
            {tickFormat(tick)}
          </text>
        </g>
      ))}
      <line x1={scale.range()[0]} x2={scale.range()[1]} strokeWidth={1} />
    </g>
  );
};

const BarGraphHoriz = props => {
  const yRange = props.yScale('original').range()
  return (<svg width={props.size[0]} height={props.size[1]}>
    <g transform={`translate(0,${yRange[0]})`}>
      <XAxis 
        scale={props.xScale} 
        gridHeight={yRange[0] - yRange[1]}
        numTicks={props.numTicks}
        format={props.xFormat}/>
    </g>
    {["original", "proposed"].map(side => {
      const yScale = props.yScale(side)
      return (<g 
        className="timeline" 
        key={side}>
          <g className="bar-graph bar-graph-horiz">
            {props.data[side].map((d,i) => {
              const x = props.xScale(d[props.x]),
                zero = props.xScale(0);
              const tooShort = Math.abs(x - zero) < 40;
              const negative = d[props.x] < 0;
              const height = yScale.bandwidth()/2;
              return (<g key={i}>
                  <rect
                    className="rect"
                    key={d[props.y]}
                    x={negative ? x : zero}
                    width={negative ? zero - x : x - zero}
                    y={yScale(i) + (side === "proposed" ? height : 0)}
                    height={height}
                    fill={props.colorScale(side)}/>
                {side === "original" 
                  || i > props.data.original.length - 1
                  || d[props.y] !== props.data.original[i][props.y]
                  ? <text 
                    className={Classnames("ticker", { "exit": d.exit })}
                    y={yScale(i) + 5 + (side === "proposed" ? 1.5*height :  .5*height)}>
                    {d[props.y]}
                  </text>
                  : null}
                {!!d[props.x]
                  ? <text
                      className={Classnames("text", { "outside": tooShort })}
                      y={yScale(i) + 5 + (side === "proposed" ? 1.5*height : .5*height)}
                      x={x + (negative ? -1 : 1)*(tooShort ? 20 : -25)}>
                      {format(props.xFormat)(d[props.x])}
                    </text>
                  : null}
              </g>);
            })}
          </g>
        </g>)
    })}
  </svg>);
}

const mapStateToProps = createStructuredSelector({
  data,
  x,
  y,
  xScale,
  yScale,
  size,
  totalSize,
  gutter,
  numTicks,
});

export default connect(mapStateToProps)(BarGraphHoriz);



// WEBPACK FOOTER //
// ./src/components/BarGraphHoriz/index.js